import { createStore, del, entries, get, set } from "idb-keyval";
import { debounce } from "@excalidraw/common";
import { clearAppStateForLocalStorage } from "@excalidraw/excalidraw/appState";
import {
  getNonDeletedElements,
  hashElementsVersion,
  isInitializedImageElement,
} from "@excalidraw/element";

import type { ExcalidrawElement, FileId } from "@excalidraw/element/types";
import type {
  AppState,
  BinaryFileData,
  BinaryFiles,
  DataURL,
} from "@excalidraw/excalidraw/types";

import {
  SAVE_TO_LOCAL_STORAGE_TIMEOUT,
  SAVE_TO_REMOTE_TIMEOUT,
  STORAGE_KEYS,
} from "../app_constants";

import {
  boardFileObjectKey,
  boardsApi,
  isBoardsApiEnabled,
  type BoardMeta,
  type BoardScenePayload,
  type RemoteFileRef,
} from "./boardsApi";
import { importFromLocalStorage } from "./localStorage";
import { LocalData } from "./LocalData";

const boardsStore = createStore(
  `${STORAGE_KEYS.IDB_BOARDS}-db`,
  `${STORAGE_KEYS.IDB_BOARDS}-store`,
);

const META_KEY = "meta:list";
const sceneKey = (boardId: string) => `scene:${boardId}`;
const fileKey = (boardId: string, fileId: string) =>
  `file:${boardId}:${fileId}`;

const createId = () => crypto.randomUUID();

type StoredScene = {
  elements: readonly ExcalidrawElement[];
  appState: Partial<AppState> | null;
};

export type BoardSceneData = {
  elements: readonly ExcalidrawElement[];
  appState: Partial<AppState> | null;
  files: BinaryFiles;
};

let currentBoardId: string | null = null;
let initialized = false;
let initPromise: Promise<string> | null = null;

export type BoardSaveStatusState =
  | { state: "idle" }
  | { state: "saving"; manual?: boolean }
  | { state: "saved"; at: number; manual?: boolean }
  | { state: "error"; message: string };

export type BoardLoadProgress = {
  boardId: string;
  phase: "saving-current" | "loading-scene" | "loading-files" | "done";
  message: string;
  loadedFiles?: number;
  totalFiles?: number;
};

type SaveStatusListener = (status: BoardSaveStatusState) => void;
const saveStatusListeners = new Set<SaveStatusListener>();

export const subscribeBoardSaveStatus = (listener: SaveStatusListener) => {
  saveStatusListeners.add(listener);
  return () => {
    saveStatusListeners.delete(listener);
  };
};

const emitSaveStatus = (status: BoardSaveStatusState) => {
  for (const listener of saveStatusListeners) {
    listener(status);
  }
};

/** Last successfully persisted content fingerprint per board (skip unchanged autosaves). */
const lastSavedFingerprintByBoard = new Map<string, string>();

const readMetaList = async (): Promise<BoardMeta[]> => {
  return (await get<BoardMeta[]>(META_KEY, boardsStore)) || [];
};

const writeMetaList = async (boards: BoardMeta[]) => {
  await set(META_KEY, boards, boardsStore);
};

const getCurrentBoardIdFromLS = () => {
  try {
    return localStorage.getItem(STORAGE_KEYS.LOCAL_STORAGE_CURRENT_BOARD_ID);
  } catch {
    return null;
  }
};

const setCurrentBoardIdToLS = (boardId: string) => {
  try {
    localStorage.setItem(STORAGE_KEYS.LOCAL_STORAGE_CURRENT_BOARD_ID, boardId);
  } catch (error) {
    console.warn(error);
  }
};

const upsertMeta = async (board: BoardMeta) => {
  const boards = await readMetaList();
  const index = boards.findIndex((item) => item.id === board.id);
  if (index >= 0) {
    boards[index] = board;
  } else {
    boards.unshift(board);
  }
  boards.sort((a, b) => b.updatedAt - a.updatedAt);
  await writeMetaList(boards);
  return boards;
};

const loadLocalFiles = async (boardId: string): Promise<BinaryFiles> => {
  const all = await entries(boardsStore);
  const files: BinaryFiles = {};
  const prefix = `file:${boardId}:`;
  for (const [key, value] of all) {
    if (typeof key === "string" && key.startsWith(prefix) && value) {
      const fileId = key.slice(prefix.length) as FileId;
      files[fileId] = value as BinaryFileData;
    }
  }
  return files;
};

const saveLocalFiles = async (boardId: string, files: BinaryFiles) => {
  await Promise.all(
    Object.entries(files).map(([fileId, fileData]) =>
      set(fileKey(boardId, fileId), fileData, boardsStore),
    ),
  );
};

const collectUsedFiles = (
  elements: readonly ExcalidrawElement[],
  files: BinaryFiles,
): BinaryFiles => {
  const used: BinaryFiles = {};
  for (const element of elements) {
    if (isInitializedImageElement(element) && files[element.fileId]) {
      used[element.fileId] = files[element.fileId];
    }
  }
  return used;
};

const getContentFingerprint = (
  boardId: string,
  elements: readonly ExcalidrawElement[],
  appState: AppState | Partial<AppState> | null,
  files: BinaryFiles,
) => {
  const nonDeleted = getNonDeletedElements(elements);
  const usedFiles = collectUsedFiles(nonDeleted, files);
  const filesSig = Object.keys(usedFiles)
    .sort()
    .map((id) => `${id}:${usedFiles[id].version || 1}`)
    .join(",");
  // Canvas content only — ignore selection/zoom/tool UI churn from onChange
  const bg =
    typeof appState?.viewBackgroundColor === "string"
      ? appState.viewBackgroundColor
      : "";
  const name = typeof appState?.name === "string" ? appState.name : "";
  const grid = [
    appState?.gridModeEnabled ? 1 : 0,
    appState?.gridSize ?? "",
    appState?.gridStep ?? "",
  ].join(":");
  return `${boardId}|e:${hashElementsVersion(nonDeleted)}|f:${filesSig}|bg:${bg}|n:${name}|g:${grid}`;
};

const isSceneDirty = (
  boardId: string,
  elements: readonly ExcalidrawElement[],
  appState: AppState,
  files: BinaryFiles,
) => {
  const fingerprint = getContentFingerprint(boardId, elements, appState, files);
  return lastSavedFingerprintByBoard.get(boardId) !== fingerprint;
};

const markSceneSaved = (
  boardId: string,
  elements: readonly ExcalidrawElement[],
  appState: AppState | Partial<AppState> | null,
  files: BinaryFiles,
) => {
  lastSavedFingerprintByBoard.set(
    boardId,
    getContentFingerprint(boardId, elements, appState, files),
  );
};

const saveLocalScene = async (boardId: string, scene: StoredScene) => {
  await set(sceneKey(boardId), scene, boardsStore);
};

const loadLocalScene = async (boardId: string): Promise<StoredScene | null> => {
  return (await get<StoredScene>(sceneKey(boardId), boardsStore)) || null;
};

const mirrorActiveBoardToLegacyLocalStorage = (
  elements: readonly ExcalidrawElement[],
  appState: AppState,
) => {
  try {
    localStorage.setItem(
      STORAGE_KEYS.LOCAL_STORAGE_ELEMENTS,
      JSON.stringify(getNonDeletedElements(elements)),
    );
    localStorage.setItem(
      STORAGE_KEYS.LOCAL_STORAGE_APP_STATE,
      JSON.stringify(clearAppStateForLocalStorage(appState)),
    );
  } catch (error) {
    console.warn(error);
  }
};

const toRemoteFileRefs = (
  boardId: string,
  files: BinaryFiles,
): Record<string, RemoteFileRef> => {
  const refs: Record<string, RemoteFileRef> = {};
  for (const [fileId, file] of Object.entries(files)) {
    refs[fileId] = {
      id: fileId,
      mimeType: file.mimeType,
      created: file.created,
      version: file.version || 1,
      key: boardFileObjectKey(boardId, fileId),
    };
  }
  return refs;
};

const buildScenePayload = (
  boardId: string,
  elements: readonly ExcalidrawElement[],
  appState: AppState,
  files: BinaryFiles,
): BoardScenePayload => ({
  elements: getNonDeletedElements(elements) as unknown[],
  appState: clearAppStateForLocalStorage(appState) as unknown as Record<
    string,
    unknown
  >,
  files: toRemoteFileRefs(boardId, collectUsedFiles(elements, files)),
});

const fileNeedsUpload = (
  local: BinaryFileData,
  remote: RemoteFileRef | Record<string, unknown> | undefined,
) => {
  if (!remote || typeof remote !== "object") {
    return true;
  }
  // Legacy embedded dataURL still on server — re-upload as R2 object
  if ("dataURL" in remote && typeof remote.dataURL === "string") {
    return true;
  }
  const remoteVersion =
    typeof (remote as RemoteFileRef).version === "number"
      ? (remote as RemoteFileRef).version
      : 1;
  const localVersion = local.version || 1;
  return localVersion !== remoteVersion;
};

const uploadFilesToRemote = async (
  boardId: string,
  files: BinaryFiles,
  remoteFiles: Record<string, RemoteFileRef | Record<string, unknown>> = {},
) => {
  const uploads = Object.values(files).map(async (file) => {
    if (!fileNeedsUpload(file, remoteFiles[file.id])) {
      return;
    }
    await boardsApi.putFile(boardId, {
      id: file.id,
      mimeType: file.mimeType,
      dataURL: file.dataURL,
      created: file.created,
      version: file.version || 1,
    });
  });
  await Promise.all(uploads);
};

const hydrateRemoteFiles = async (
  boardId: string,
  remoteFiles: Record<string, RemoteFileRef | Record<string, unknown>> = {},
  onFileProgress?: (loaded: number, total: number) => void,
): Promise<BinaryFiles> => {
  const entriesList = Object.entries(remoteFiles);
  const total = entriesList.length;
  let loaded = 0;
  const files: BinaryFiles = {};

  if (total === 0) {
    onFileProgress?.(0, 0);
    return files;
  }

  await Promise.all(
    entriesList.map(async ([fileId, raw]) => {
      if (!raw || typeof raw !== "object") {
        loaded += 1;
        onFileProgress?.(loaded, total);
        return;
      }
      // Compat: still-embedded dataURL from pre-migration scenes
      if (
        typeof (raw as { dataURL?: string }).dataURL === "string" &&
        (raw as { dataURL: string }).dataURL.startsWith("data:")
      ) {
        const embedded = raw as {
          dataURL: string;
          mimeType?: string;
          created?: number;
          version?: number;
        };
        files[fileId as FileId] = {
          id: fileId as FileId,
          mimeType: (embedded.mimeType ||
            "image/png") as BinaryFileData["mimeType"],
          dataURL: embedded.dataURL as DataURL,
          created: embedded.created || Date.now(),
          version: embedded.version || 1,
          lastRetrieved: Date.now(),
        };
        loaded += 1;
        onFileProgress?.(loaded, total);
        return;
      }

      try {
        const downloaded = await boardsApi.getFile(boardId, fileId);
        files[fileId as FileId] = {
          id: fileId as FileId,
          mimeType: downloaded.mimeType as BinaryFileData["mimeType"],
          dataURL: downloaded.dataURL as DataURL,
          created: downloaded.created,
          version: downloaded.version,
          lastRetrieved: Date.now(),
        };
      } catch (error) {
        console.warn("Failed loading remote file", fileId, error);
      } finally {
        loaded += 1;
        onFileProgress?.(loaded, total);
      }
    }),
  );
  return files;
};

const pushSceneToRemote = async (
  boardId: string,
  elements: readonly ExcalidrawElement[],
  appState: AppState,
  files: BinaryFiles,
  opts?: { keepalive?: boolean; manual?: boolean; silent?: boolean },
) => {
  if (!isBoardsApiEnabled()) {
    markSceneSaved(boardId, elements, appState, collectUsedFiles(elements, files));
    if (!opts?.silent) {
      emitSaveStatus({ state: "saved", at: Date.now(), manual: opts?.manual });
    }
    return null;
  }
  if (!opts?.silent) {
    emitSaveStatus({ state: "saving", manual: opts?.manual });
  }
  try {
    const usedFiles = collectUsedFiles(elements, files);
    let remoteFiles: Record<string, RemoteFileRef | Record<string, unknown>> =
      {};
    try {
      const remoteScene = await boardsApi.getScene(boardId);
      remoteFiles = remoteScene.files || {};
    } catch {
      // New board or offline — upload all used files
    }

    // Upload image bytes first (keepalive cannot reliably send large bodies)
    await uploadFilesToRemote(boardId, usedFiles, remoteFiles);

    const result = await boardsApi.putScene(
      boardId,
      buildScenePayload(boardId, elements, appState, usedFiles),
      opts,
    );
    const existing = (await readMetaList()).find(
      (board) => board.id === boardId,
    );
    await upsertMeta({
      id: boardId,
      title: existing?.title || "未命名画板",
      createdAt: existing?.createdAt || Date.now(),
      updatedAt: result.updatedAt || Date.now(),
    });
    markSceneSaved(boardId, elements, appState, usedFiles);
    if (!opts?.silent) {
      emitSaveStatus({
        state: "saved",
        at: Date.now(),
        manual: opts?.manual,
      });
    }
    // Refresh project-card thumbnail in the background
    void import("./boardPreview")
      .then(({ uploadBoardPreviewFromScene }) =>
        uploadBoardPreviewFromScene(boardId, elements, appState, usedFiles),
      )
      .catch((error) => {
        console.warn("Failed updating board preview", error);
      });
    return result;
  } catch (error) {
    console.warn("Failed to sync board scene to remote", error);
    if (!opts?.silent) {
      emitSaveStatus({
        state: "error",
        message: error instanceof Error ? error.message : String(error),
      });
    }
    return null;
  }
};

const syncSceneToRemote = debounce(
  (
    boardId: string,
    elements: readonly ExcalidrawElement[],
    appState: AppState,
    files: BinaryFiles,
  ) => {
    void pushSceneToRemote(boardId, elements, appState, files);
  },
  SAVE_TO_REMOTE_TIMEOUT,
);

const createDefaultBoard = async (title = "画板 1"): Promise<BoardMeta> => {
  const now = Date.now();
  const board: BoardMeta = {
    id: createId(),
    title,
    createdAt: now,
    updatedAt: now,
  };
  await saveLocalScene(board.id, { elements: [], appState: null });
  await writeMetaList([board]);

  if (isBoardsApiEnabled()) {
    try {
      const { board: remote } = await boardsApi.create({
        id: board.id,
        title: board.title,
        content: { elements: [], appState: {}, files: {} },
      });
      await writeMetaList([remote]);
      return remote;
    } catch (error) {
      console.warn("Failed creating default remote board", error);
    }
  }

  return board;
};

const migrateLegacyIfNeeded = async (): Promise<BoardMeta[]> => {
  const existing = await readMetaList();
  if (existing.length) {
    return existing;
  }

  const legacy = importFromLocalStorage();
  const hasLegacy =
    (legacy.elements && legacy.elements.length > 0) || !!legacy.appState;
  if (!hasLegacy) {
    return [];
  }

  const now = Date.now();
  const board: BoardMeta = {
    id: createId(),
    title: "画板 1",
    createdAt: now,
    updatedAt: now,
  };

  await saveLocalScene(board.id, {
    elements: legacy.elements || [],
    appState: legacy.appState,
  });

  try {
    const fileIds = (legacy.elements || [])
      .map((element) => ("fileId" in element ? element.fileId : null))
      .filter(Boolean) as FileId[];

    if (fileIds.length) {
      const { loadedFiles } = await LocalData.fileStorage.getFiles(fileIds);
      const files: BinaryFiles = {};
      for (const file of loadedFiles) {
        files[file.id] = file;
      }
      await saveLocalFiles(board.id, files);
    }
  } catch (error) {
    console.warn("Failed migrating legacy image files", error);
  }

  await writeMetaList([board]);

  if (isBoardsApiEnabled()) {
    try {
      const files = await loadLocalFiles(board.id);
      const { board: remote } = await boardsApi.create({
        id: board.id,
        title: board.title,
        content: {
          elements: (legacy.elements || []) as unknown[],
          appState: (legacy.appState || {}) as Record<string, unknown>,
          files: {},
        },
      });
      if (Object.keys(files).length) {
        await uploadFilesToRemote(board.id, files, {});
        await boardsApi.putScene(board.id, {
          elements: (legacy.elements || []) as unknown[],
          appState: (legacy.appState || {}) as Record<string, unknown>,
          files: toRemoteFileRefs(board.id, files),
        });
      }
      await writeMetaList([remote]);
      return [remote];
    } catch (error) {
      console.warn("Failed creating remote board during migration", error);
    }
  }

  return [board];
};

/** Remote is source of truth for cross-device sync. */
const syncMetaFromRemote = async (): Promise<BoardMeta[]> => {
  if (!isBoardsApiEnabled()) {
    return readMetaList();
  }

  try {
    const { boards: remoteBoards } = await boardsApi.list();
    const localBoards = await readMetaList();
    const remoteIds = new Set(remoteBoards.map((board) => board.id));

    // Upload local-only boards that never made it to the server.
    // Skip empty stubs when cloud already has boards — avoids a fresh browser
    // (or a previous offline default) polluting remote with a blank board.
    for (const local of localBoards) {
      if (remoteIds.has(local.id)) {
        continue;
      }
      try {
        const scene = await loadLocalScene(local.id);
        const files = await loadLocalFiles(local.id);
        const hasContent =
          (scene?.elements?.length || 0) > 0 || Object.keys(files).length > 0;
        if (!hasContent && remoteBoards.length > 0) {
          continue;
        }
        // Create board first, then upload files + scene refs (avoids huge POST)
        await boardsApi.create({
          id: local.id,
          title: local.title,
          content: {
            elements: (scene?.elements || []) as unknown[],
            appState: (scene?.appState || {}) as Record<string, unknown>,
            files: {},
          },
        });
        if (Object.keys(files).length) {
          await uploadFilesToRemote(local.id, files, {});
          await boardsApi.putScene(local.id, {
            elements: (scene?.elements || []) as unknown[],
            appState: (scene?.appState || {}) as Record<string, unknown>,
            files: toRemoteFileRefs(local.id, files),
          });
        }
      } catch (error) {
        console.warn("Failed uploading local-only board", local.id, error);
      }
    }

    const { boards: latestRemote } = await boardsApi.list();
    await writeMetaList(latestRemote);
    return latestRemote;
  } catch (error) {
    console.warn("Failed listing remote boards", error);
    return readMetaList();
  }
};

export const BoardStorage = {
  async init() {
    if (initialized) {
      return this.getCurrentBoardId()!;
    }
    if (initPromise) {
      return initPromise;
    }

    initPromise = (async () => {
      // 1) Prefer cloud list so a fresh browser sees existing boards
      let boards = await syncMetaFromRemote();

      // 2) Only if cloud+local are empty, migrate legacy or create default
      if (!boards.length) {
        boards = await migrateLegacyIfNeeded();
      }
      if (!boards.length) {
        boards = [await createDefaultBoard("画板 1")];
      }

      let remoteActiveId: string | null = null;
      if (isBoardsApiEnabled()) {
        try {
          remoteActiveId = (await boardsApi.getSettings()).activeBoardId;
        } catch (error) {
          console.warn("Failed loading remote active board", error);
        }
      }

      const fromUrl = new URLSearchParams(window.location.search).get("board");
      const fromHash = window.location.hash.match(/^#board=([a-zA-Z0-9_-]+)$/);
      const fromLs = getCurrentBoardIdFromLS();
      // Prefer explicit board URL even if meta cache is briefly stale
      if (fromUrl && !boards.find((board) => board.id === fromUrl)) {
        const now = Date.now();
        await upsertMeta({
          id: fromUrl,
          title: "画板",
          createdAt: now,
          updatedAt: now,
        });
        boards = await readMetaList();
      }
      // Fresh browser: use cloud "last used" board, not a newly created blank one
      const preferred =
        boards.find((board) => board.id === fromUrl)?.id ||
        boards.find((board) => board.id === fromHash?.[1])?.id ||
        boards.find((board) => board.id === remoteActiveId)?.id ||
        boards.find((board) => board.id === fromLs)?.id ||
        boards[0].id;

      currentBoardId = preferred;
      setCurrentBoardIdToLS(preferred);
      if (isBoardsApiEnabled()) {
        void boardsApi.setActiveBoard(preferred).catch((error) => {
          console.warn("Failed syncing active board", error);
        });
      }
      initialized = true;
      return preferred;
    })();

    try {
      return await initPromise;
    } finally {
      initPromise = null;
    }
  },

  getCurrentBoardId() {
    return currentBoardId || getCurrentBoardIdFromLS();
  },

  async listBoards() {
    await this.init();
    return syncMetaFromRemote();
  },

  /** Prefer IndexedDB cache; only hit the API when local list is empty. */
  async listBoardsLocalFirst() {
    await this.init();
    const local = await readMetaList();
    if (local.length > 0) {
      return local;
    }
    return syncMetaFromRemote();
  },

  /** Force-pull remote list and overwrite local meta cache. */
  async refreshBoardsFromRemote() {
    await this.init();
    if (!isBoardsApiEnabled()) {
      return readMetaList();
    }
    const { boards } = await boardsApi.list();
    await writeMetaList(boards);
    return boards;
  },

  async createBoard(title = "未命名画板", projectId?: string | null) {
    await this.init();
    const now = Date.now();
    const fromUrlProject =
      projectId ??
      new URLSearchParams(window.location.search).get("project");
    const board: BoardMeta = {
      id: createId(),
      title,
      projectId: fromUrlProject,
      createdAt: now,
      updatedAt: now,
    };

    await saveLocalScene(board.id, { elements: [], appState: null });
    await upsertMeta(board);

    if (isBoardsApiEnabled()) {
      try {
        const { board: remote } = await boardsApi.create({
          id: board.id,
          title: board.title,
          projectId: fromUrlProject,
          content: { elements: [], appState: {}, files: {} },
        });
        await upsertMeta(remote);
      } catch (error) {
        console.warn("Failed creating remote board", error);
      }
    }

    return syncMetaFromRemote();
  },

  async renameBoard(boardId: string, title: string) {
    const boards = await readMetaList();
    const board = boards.find((item) => item.id === boardId);
    if (!board) {
      return boards;
    }
    const next = {
      ...board,
      title: title.trim() || "未命名画板",
      updatedAt: Date.now(),
    };
    await upsertMeta(next);

    if (isBoardsApiEnabled()) {
      try {
        const { board: remote } = await boardsApi.update(boardId, {
          title: next.title,
        });
        await upsertMeta(remote);
      } catch (error) {
        console.warn("Failed renaming remote board", error);
      }
    }

    return syncMetaFromRemote();
  },

  async deleteBoard(boardId: string) {
    let boards = await readMetaList();
    if (boards.length <= 1) {
      throw new Error("至少保留一个画板");
    }

    boards = boards.filter((board) => board.id !== boardId);
    await writeMetaList(boards);
    await del(sceneKey(boardId), boardsStore);

    const all = await entries(boardsStore);
    await Promise.all(
      all.map(([key]) => {
        if (typeof key === "string" && key.startsWith(`file:${boardId}:`)) {
          return del(key, boardsStore);
        }
        return undefined;
      }),
    );

    if (isBoardsApiEnabled()) {
      try {
        await boardsApi.remove(boardId);
      } catch (error) {
        console.warn("Failed deleting remote board", error);
      }
    }

    if (this.getCurrentBoardId() === boardId) {
      const nextId = boards[0].id;
      // Cancel any in-flight autosave that still holds the deleted board's scene,
      // otherwise it can overwrite the next board after we point currentBoardId at it.
      this.cancelPendingSave();
      lastSavedFingerprintByBoard.delete(boardId);
      currentBoardId = nextId;
      setCurrentBoardIdToLS(nextId);
    }

    return syncMetaFromRemote();
  },

  async setCurrentBoard(boardId: string) {
    await this.init();
    // Refresh list so newly synced remote boards are visible
    const boards = await syncMetaFromRemote();
    if (!boards.some((board) => board.id === boardId)) {
      throw new Error("Board not found");
    }
    currentBoardId = boardId;
    setCurrentBoardIdToLS(boardId);

    const url = new URL(window.location.href);
    url.searchParams.set("board", boardId);
    window.history.replaceState({}, document.title, url.toString());

    if (isBoardsApiEnabled()) {
      try {
        await boardsApi.setActiveBoard(boardId);
      } catch (error) {
        console.warn("Failed syncing active board", error);
      }
    }
  },

  async loadScene(
    boardId?: string,
    onProgress?: (progress: BoardLoadProgress) => void,
  ): Promise<BoardSceneData> {
    await this.init();
    const id = boardId || this.getCurrentBoardId();
    if (!id) {
      return { elements: [], appState: null, files: {} };
    }

    // Cross-device: always prefer remote when API is available
    if (isBoardsApiEnabled()) {
      try {
        onProgress?.({
          boardId: id,
          phase: "loading-scene",
          message: "正在加载画板…",
        });
        const remote = await boardsApi.getScene(id);
        const local: StoredScene = {
          elements: (remote.elements || []) as ExcalidrawElement[],
          appState: (remote.appState || null) as Partial<AppState> | null,
        };
        const remoteFileCount = Object.keys(remote.files || {}).length;
        if (remoteFileCount > 0) {
          onProgress?.({
            boardId: id,
            phase: "loading-files",
            message: `正在加载图片 0/${remoteFileCount}`,
            loadedFiles: 0,
            totalFiles: remoteFileCount,
          });
        }
        const files = await hydrateRemoteFiles(
          id,
          remote.files || {},
          (loaded, total) => {
            onProgress?.({
              boardId: id,
              phase: "loading-files",
              message:
                total > 0
                  ? `正在加载图片 ${loaded}/${total}`
                  : "正在加载画板…",
              loadedFiles: loaded,
              totalFiles: total,
            });
          },
        );
        await saveLocalScene(id, local);
        await saveLocalFiles(id, files);
        markSceneSaved(id, local.elements, local.appState, files);
        onProgress?.({
          boardId: id,
          phase: "done",
          message: "加载完成",
          loadedFiles: remoteFileCount,
          totalFiles: remoteFileCount,
        });
        return {
          elements: local.elements,
          appState: local.appState,
          files,
        };
      } catch (error) {
        console.warn("Failed loading remote board scene, using local", error);
      }
    }

    const local = await loadLocalScene(id);
    const files = await loadLocalFiles(id);
    markSceneSaved(
      id,
      local?.elements || [],
      local?.appState || null,
      files,
    );
    onProgress?.({
      boardId: id,
      phase: "done",
      message: "加载完成",
    });
    return {
      elements: local?.elements || [],
      appState: local?.appState || null,
      files,
    };
  },

  save(
    elements: readonly ExcalidrawElement[],
    appState: AppState,
    files: BinaryFiles,
    onFilesSaved: () => void,
  ) {
    if (document.hidden || LocalData.isSavePaused()) {
      return;
    }

    const boardId = this.getCurrentBoardId();
    if (!boardId) {
      LocalData.save(elements, appState, files, onFilesSaved);
      return;
    }

    // Autosave only when canvas content actually changed
    if (!isSceneDirty(boardId, elements, appState, files)) {
      return;
    }

    this._save(boardId, elements, appState, files, onFilesSaved);
  },

  flushSave() {
    this._save.flush();
    syncSceneToRemote.flush();
    LocalData.flushSave();
  },

  /** Drop pending debounced saves without writing (e.g. after deleting the active board). */
  cancelPendingSave() {
    this._save.cancel();
    syncSceneToRemote.cancel();
  },

  /** Force an immediate remote sync (e.g. before switching boards / unload / Ctrl+S). */
  async syncNow(
    elements: readonly ExcalidrawElement[],
    appState: AppState,
    files: BinaryFiles,
    opts?: { keepalive?: boolean; manual?: boolean; silent?: boolean },
  ) {
    const boardId = this.getCurrentBoardId();
    if (!boardId) {
      return;
    }

    const dirty = isSceneDirty(boardId, elements, appState, files);
    // Autosave / silent paths: skip network if unchanged.
    // Manual Ctrl+S: still acknowledge UI, but skip upload when clean.
    if (!dirty) {
      if (opts?.manual && !opts?.silent) {
        emitSaveStatus({ state: "saved", at: Date.now(), manual: true });
      }
      return;
    }

    this._save.flush();
    syncSceneToRemote.cancel();
    await pushSceneToRemote(boardId, elements, appState, files, {
      keepalive: opts?.keepalive ?? true,
      manual: opts?.manual,
      silent: opts?.silent,
    });
  },

  _save: debounce(
    async (
      boardId: string,
      elements: readonly ExcalidrawElement[],
      appState: AppState,
      files: BinaryFiles,
      onFilesSaved: () => void,
    ) => {
      // Re-check after debounce — another change may have been reverted
      if (!isSceneDirty(boardId, elements, appState, files)) {
        onFilesSaved();
        return;
      }

      const scene: StoredScene = {
        elements: getNonDeletedElements(elements),
        appState: clearAppStateForLocalStorage(appState),
      };

      const usedFiles = collectUsedFiles(elements, files);
      await saveLocalScene(boardId, scene);
      await saveLocalFiles(boardId, usedFiles);
      mirrorActiveBoardToLegacyLocalStorage(elements, appState);

      const boards = await readMetaList();
      const board = boards.find((item) => item.id === boardId);
      if (board) {
        await upsertMeta({ ...board, updatedAt: Date.now() });
      }

      await LocalData.fileStorage.saveFiles({
        elements,
        files,
      });
      onFilesSaved();

      syncSceneToRemote(boardId, elements, appState, files);
    },
    SAVE_TO_LOCAL_STORAGE_TIMEOUT,
  ),
};
